'use client';

import { RefreshCw, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useVersionCheck } from './useVersionCheck';

// Machine-pref key (NOT the `lvdr_session_` prefix and not on hardLogout's
// allowlist) so a dismissal survives logout — same call as InstallBanner's. This
// is a device UI preference, not session-tied account data.
//
// The dismissal is keyed BY VERSION: dismissing "update to X" must not suppress
// the banner for a later build Y. A single boolean key would silence every
// future update on that device — permanently, and invisibly.
const DISMISS_PREFIX = 'lvdr:pwa-update-dismissed:';

/**
 * UpdateBanner — tells a long-running session that a different build is being
 * served, and offers a reload.
 *
 * Rendered in-flow in the app banner stack beside InstallBanner. Renders nothing
 * when the running build matches what is served, which is the overwhelmingly
 * common case.
 *
 * The reload is USER-INITIATED, never automatic, and that is a safety property
 * rather than a UX preference. An auto-reload on version mismatch is a reload
 * loop the moment the served version and the bundle version can disagree for any
 * reason — a half-published deploy, a CDN serving two origins, a proxy rewriting
 * the response. It would also discard whatever the user was typing. A button the
 * user presses cannot loop without them.
 */
export function UpdateBanner() {
  const t = useTranslations('pwa');
  const { updateAvailable, servedVersion } = useVersionCheck();
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!servedVersion) return;
    try {
      if (window.localStorage.getItem(DISMISS_PREFIX + servedVersion) === '1') {
        setDismissedVersion(servedVersion);
      }
    } catch {
      /* private mode / storage disabled — no persisted dismissal to read */
    }
  }, [servedVersion]);

  if (!updateAvailable) return null;
  if (servedVersion && dismissedVersion === servedVersion) return null;

  const handleDismiss = () => {
    setDismissedVersion(servedVersion);
    try {
      if (servedVersion) window.localStorage.setItem(DISMISS_PREFIX + servedVersion, '1');
    } catch {
      /* dismiss for this session only */
    }
  };

  return (
    <aside
      aria-label={t('update.title')}
      className="border border-accent/20 bg-accent/10 text-foreground rounded-lg px-4 py-3 text-sm flex items-start gap-3"
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium">{t('update.title')}</p>
        <p className="text-muted-foreground">{t('update.body')}</p>
      </div>
      <Button
        size="sm"
        onClick={() => window.location.reload()}
        className="gap-1.5 shrink-0 self-center"
      >
        <RefreshCw className="h-4 w-4" />
        {t('update.action')}
      </Button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('update.dismiss')}
        className="text-muted-foreground hover:text-foreground shrink-0 self-start"
      >
        <X className="h-4 w-4" />
      </button>
    </aside>
  );
}
